import { z } from "zod";

export const MachineStatusChangedSchema = z.object({
  machineId: z.string().uuid(),
  lineId: z.string().uuid(),
  status: z.enum(["RUNNING", "STOPPED", "IDLE", "NODATA"]),
  statusSince: z.string().datetime(),
  previousStatus: z.enum(["RUNNING", "STOPPED", "IDLE", "NODATA"]).nullable(),
});
export type MachineStatusChanged = z.infer<typeof MachineStatusChangedSchema>;

export const ProductionUpdatedSchema = z.object({
  runId: z.string().uuid(),
  lineId: z.string().uuid(),
  status: z.enum(["ACTIVE", "PAUSED", "COMPLETED"]),
  producedQty: z.number().int().nonnegative(),
  plannedQty: z.number().int().positive(),
  startedAt: z.string().datetime(),
  pausedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
});
export type ProductionUpdated = z.infer<typeof ProductionUpdatedSchema>;

export const AlarmCreatedSchema = z.object({
  alarmId: z.string().uuid(),
  machineId: z.string().uuid(),
  lineId: z.string().uuid(),
  kind: z.enum(["INFO", "ALERT", "ALARM"]),
  severity: z.enum(["LOW", "MEDIUM", "HIGH"]),
  state: z.enum(["UNACKNOWLEDGED", "ACKNOWLEDGED", "RESOLVED", "SHELVED"]),
  suppressed: z.boolean(),
  groupLeaderId: z.string().uuid().nullable().optional(),
  title: z.string(),
  expectedAction: z.string(),
  // Instabilidade da fonte: oscilando + confiabilidade resultante.
  flapping: z.boolean(),
  reliability: z.enum(["HIGH", "MEDIUM", "LOW", "SUSPECT"]),
});
export type AlarmCreated = z.infer<typeof AlarmCreatedSchema>;

export const AlarmStateChangedSchema = z.object({
  alarmId: z.string().uuid(),
  state: z.enum(["UNACKNOWLEDGED", "ACKNOWLEDGED", "RESOLVED", "SHELVED"]),
  by: z.string().uuid().nullable().optional(),
  at: z.string().datetime(),
  cause: z.string().nullable().optional(),
  falseJustify: z.string().nullable().optional(),
});
export type AlarmStateChanged = z.infer<typeof AlarmStateChangedSchema>;

export const OeeUpdatedSchema = z.object({
  lineId: z.string().uuid(),
  period: z.enum(["8h", "24h", "7d", "30d"]),
  availability: z.number().nullable(),
  performance: z.number().nullable(),
  quality: z.number().nullable(),
  oee: z.number().nullable(),
  confidenceLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]),
  reasons: z.array(z.string()),
});
export type OeeUpdated = z.infer<typeof OeeUpdatedSchema>;

// Mudança de configuração da máquina (ex.: requiresOperator);
// distinto de MACHINE_STATUS_CHANGED, que é transição de status operacional.
export const MachineUpdatedSchema = z.object({
  machineId: z.string().uuid(),
  lineId: z.string().uuid(),
});
export type MachineUpdated = z.infer<typeof MachineUpdatedSchema>;

export const JoinLineSchema = z.object({ lineId: z.string().uuid() });
export type JoinLine = z.infer<typeof JoinLineSchema>;

export const LeaveLineSchema = JoinLineSchema;
export type LeaveLine = z.infer<typeof LeaveLineSchema>;

export const WS_EVENTS = {
  MACHINE_STATUS_CHANGED: "machine:status:changed",
  MACHINE_UPDATED: "machine:updated",
  PRODUCTION_UPDATED: "production:updated",
  ALARM_CREATED: "alarm:created",
  ALARM_STATE_CHANGED: "alarm:state:changed",
  OEE_UPDATED: "oee:updated",
  JOIN_LINE: "join:line",
  LEAVE_LINE: "leave:line",
} as const;

export const SimProgressSchema = z.object({
  percent: z.number().min(0).max(100),
  step: z.string(),
});
export const SimCompletedSchema = z.object({
  executionId: z.string().uuid(),
  results: z.unknown(),
  confidenceLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]),
});
export const SimFailedSchema = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
});
